<template>
    <Teleport to="body">
        <Transition name="modal">
            <div v-if="isOpen" class="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
                <!-- Backdrop -->
                <div class="absolute inset-0 bg-black/80 backdrop-blur-md" />

                <!-- Modal -->
                <div
                    ref="modalRef"
                    class="wizard-modal relative w-full max-w-xl max-h-[100dvh] sm:max-h-[90vh] flex flex-col rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
                    :style="modalStyle"
                    @touchstart="handleTouchStart"
                    @touchmove="handleTouchMove"
                    @touchend="handleTouchEnd"
                >
                    <!-- Glow accent top edge -->
                    <div class="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-violet-500/50 to-transparent pointer-events-none" />

                    <!-- Mobile Pull Handle -->
                    <div class="sm:hidden flex justify-center pt-2 pb-0.5 cursor-grab active:cursor-grabbing shrink-0 relative z-10">
                        <div class="w-10 h-1 rounded-full bg-white/20" />
                    </div>

                    <!-- Close Button -->
                    <button
                        @click="handleClose"
                        class="absolute top-3 right-3 z-20 w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-all active:scale-95"
                        aria-label="Close"
                    >
                        <X class="w-3.5 h-3.5 text-white/60" />
                    </button>

                    <!-- ── Success State ── -->
                    <div v-if="submitted" class="px-6 pt-8 pb-8 flex flex-col items-center text-center gap-5">
                        <!-- Animated icon -->
                        <div class="relative">
                            <div class="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600/30 to-purple-600/20 border border-violet-500/30 flex items-center justify-center">
                                <Clapperboard class="w-8 h-8 text-violet-400" />
                            </div>
                            <div class="absolute -inset-1 rounded-2xl bg-violet-500/10 blur-md -z-10" />
                        </div>
                        <div>
                            <h2 class="text-xl font-bold text-white mb-1.5">Pitch Submitted!</h2>
                            <p class="text-white/50 text-sm leading-relaxed max-w-xs mx-auto">
                                Your pitch is under review. Our team will reach out to the requested performers and assess feasibility.
                            </p>
                        </div>
                        <div class="w-full bg-white/[0.03] border border-white/10 rounded-2xl px-4 py-4 text-left space-y-3">
                            <div v-for="(item, i) in successSteps" :key="i" class="flex items-start gap-3">
                                <span class="w-5 h-5 rounded-full bg-violet-600/20 border border-violet-500/30 text-violet-400 text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">{{ i + 1 }}</span>
                                <p class="text-white/55 text-xs leading-relaxed">{{ item }}</p>
                            </div>
                        </div>
                        <button
                            @click="$emit('close')"
                            class="w-full py-3 bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white font-bold rounded-xl text-sm transition-all active:scale-[0.98] shadow-lg shadow-violet-600/30"
                        >
                            Done
                        </button>
                    </div>

                    <!-- ── Wizard ── -->
                    <template v-else>

                        <!-- Title -->
                        <div class="px-5 pt-4 pb-3 shrink-0">
                            <img src="https://sm.naughtycdn.com/images/www/naughty-america-logo.png" alt="Naughty America" class="h-3.5 w-auto mb-2 opacity-60" />
                            <h2 class="text-2xl font-extrabold leading-tight pr-8">
                                <span class="text-white">Submit Your </span><span class="text-violet-400">Production Pitch</span>
                            </h2>
                            <div class="mt-2 mb-2.5 h-px bg-white/10" />
                            <p class="text-white/45 text-xs leading-relaxed">Help shape the future of our content. Share your creative ideas and help us decide what premium productions get made next.</p>
                        </div>

                        <!-- Step Indicator -->
                        <div class="px-5 pr-12 pt-3 pb-4 sm:pb-7 shrink-0 relative z-10">
                            <div class="flex items-center justify-between">
                                <template v-for="(step, i) in steps" :key="step.key">
                                    <!-- Step bubble -->
                                    <div class="relative flex flex-col items-center">
                                        <button
                                            type="button"
                                            @click="i + 1 < currentStep ? currentStep = i + 1 : null"
                                            :class="[
                                                'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300',
                                                i + 1 === currentStep
                                                    ? 'bg-violet-600 text-white shadow-lg shadow-violet-600/50 scale-110'
                                                    : i + 1 < currentStep
                                                        ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 cursor-pointer hover:bg-emerald-500/30'
                                                        : 'bg-white/5 border border-white/10 text-white/25 cursor-default'
                                            ]"
                                        >
                                            <Check v-if="i + 1 < currentStep" class="w-3.5 h-3.5" />
                                            <span v-else>{{ i + 1 }}</span>
                                        </button>
                                        <span :class="[
                                            'absolute top-full mt-1.5 text-[10px] font-semibold transition-colors duration-300 whitespace-nowrap hidden sm:block',
                                            i + 1 === currentStep ? 'text-violet-400' : i + 1 < currentStep ? 'text-emerald-500/70' : 'text-white/20'
                                        ]">{{ step.label }}</span>
                                    </div>
                                    <!-- Connector line -->
                                    <div v-if="i < steps.length - 1" class="flex-1 mx-1 sm:mx-2 h-px relative overflow-hidden">
                                        <div class="absolute inset-0 bg-white/10 rounded-full" />
                                        <div
                                            class="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all duration-500"
                                            :style="{ width: i + 1 < currentStep ? '100%' : '0%' }"
                                        />
                                    </div>
                                </template>
                            </div>
                        </div>

                        <!-- Step Body -->
                        <Transition :name="slideDirection" mode="out-in">
                            <div
                                :key="currentStep"
                                class="overflow-y-auto casting-scroll flex-1 min-h-0 px-5 pb-4 sm:px-6"
                            >
                                    <!-- ── Step 1: The Pitch ── -->
                                    <template v-if="currentStep === 1">
                                        <div class="flex items-center gap-3 mb-4 pt-1">
                                            <div class="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-600/10 border border-amber-500/20 flex items-center justify-center shrink-0">
                                                <Lightbulb class="w-4.5 h-4.5 text-amber-400" />
                                            </div>
                                            <div>
                                                <h2 class="text-base font-bold text-white leading-tight">The Pitch</h2>
                                                <p class="text-white/35 text-[11px] leading-snug">Name your idea and sell the concept.</p>
                                            </div>
                                        </div>

                                        <div class="mb-4">
                                            <label class="field-label">Pitch Title</label>
                                            <p class="text-white/35 text-[11px] mb-1.5 leading-snug">Give your pitch a name that hooks people.</p>
                                            <input
                                                v-model="form.title"
                                                type="text"
                                                placeholder="e.g. The Tutor Next Door"
                                                maxlength="120"
                                                :class="['field-input', (fieldErrors.title || stepErrors.title) ? 'field-error' : '']"
                                            />
                                            <p v-if="fieldErrors.title || stepErrors.title" class="field-error-msg">{{ fieldErrors.title || stepErrors.title }}</p>
                                        </div>

                                        <div class="mb-4">
                                            <label class="field-label">The Setup</label>
                                            <p class="text-white/35 text-[11px] mb-1.5 leading-snug">One or two sentences that sell your concept — this is what people see in the feed.</p>
                                            <textarea
                                                v-model="form.description"
                                                placeholder="e.g. A college student hires a private tutor, but the lessons quickly go off-syllabus when she realizes he's the guy she's been flirting with at the coffee shop..."
                                                rows="3"
                                                maxlength="280"
                                                :class="['field-input resize-none', (fieldErrors.description || stepErrors.description) ? 'field-error' : '']"
                                            />
                                            <div class="flex justify-between items-start mt-1">
                                                <p v-if="fieldErrors.description || stepErrors.description" class="field-error-msg">{{ fieldErrors.description || stepErrors.description }}</p>
                                                <span :class="['text-[11px] text-white/25', (fieldErrors.description || stepErrors.description) ? 'ml-auto' : 'w-full text-right']">{{ form.description.length }}/280</span>
                                            </div>
                                        </div>

                                        <div class="mb-2">
                                            <label class="field-label">Director's Notes <span class="text-white/25 font-normal">(optional)</span></label>
                                            <p class="text-white/35 text-[11px] mb-1.5 leading-snug">Describe the arc, pacing, key moments, or anything that brings your vision to life.</p>
                                            <textarea
                                                v-model="form.directorNotes"
                                                placeholder="e.g. Starts slow with tension building — eye contact, accidental touches. She makes the first move…"
                                                rows="3"
                                                maxlength="1000"
                                                :class="['field-input resize-none', fieldErrors.director_notes ? 'field-error' : '']"
                                            />
                                            <p v-if="fieldErrors.director_notes" class="field-error-msg">{{ fieldErrors.director_notes }}</p>
                                        </div>
                                    </template>

                                    <!-- ── Step 2: The Cast ── -->
                                    <template v-else-if="currentStep === 2">
                                        <div class="flex items-center gap-3 mb-4 pt-1">
                                            <div class="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500/20 to-purple-600/10 border border-violet-500/20 flex items-center justify-center shrink-0">
                                                <Users class="w-4.5 h-4.5 text-violet-400" />
                                            </div>
                                            <div>
                                                <h2 class="text-base font-bold text-white leading-tight">The Cast</h2>
                                                <p class="text-white/35 text-[11px] leading-snug">Pick performers or name specific performers.</p>
                                            </div>
                                        </div>

                                        <!-- Side-by-side: form left, featured image right -->
                                        <div class="flex gap-3 items-start mb-4">

                                            <!-- Left: performers typeahead -->
                                            <div class="flex-1 min-w-0">
                                                <label class="field-label">Performers</label>
                                                <div ref="performerInputContainer" class="relative">
                                                    <div :class="[
                                                        'bg-zinc-900/80 border rounded-xl px-3 py-2 transition-all',
                                                        fieldErrors.performers
                                                            ? 'border-red-500/60 focus-within:border-red-500'
                                                            : performerInput.trim().length >= 2 && !suggestions.length && !isLoadingPerformers
                                                                ? 'border-red-500/60'
                                                                : 'border-white/10 focus-within:border-violet-500/60 focus-within:shadow-[0_0_0_3px_rgba(139,92,246,0.08)]'
                                                    ]">
                                                        <div v-if="form.performers.length" class="flex flex-wrap gap-1.5 mb-2">
                                                            <span
                                                                v-for="performer in form.performers"
                                                                :key="performer.id"
                                                                :class="[
                                                                    'inline-flex items-center gap-1.5 border text-xs font-medium pl-0.5 pr-2 py-0.5 rounded-full cursor-pointer transition-all',
                                                                    featuredPerformerId === performer.id
                                                                        ? 'bg-violet-600/25 border-violet-500/50 text-violet-200'
                                                                        : 'bg-violet-600/15 border-violet-500/25 text-violet-300 hover:bg-violet-600/25'
                                                                ]"
                                                            >
                                                                <img :src="performer.thumbnail_url" :alt="performer.name" class="w-5 h-5 rounded-full object-cover object-top shrink-0" />
                                                                {{ performer.name }}
                                                                <button type="button" @click.stop="removePerformer(performer.id)" class="text-violet-400/50 hover:text-violet-300 transition-colors">
                                                                    <X class="w-3 h-3" />
                                                                </button>
                                                            </span>
                                                        </div>
                                                        <div v-if="form.performers.length < 4" class="flex items-center gap-2">
                                                            <input
                                                                v-model="performerInput"
                                                                type="text"
                                                                placeholder="Search performers…"
                                                                autocomplete="off"
                                                                class="flex-1 bg-transparent border-0 text-white text-sm placeholder:text-white/25 outline-none ring-0 focus:outline-none focus:ring-0"
                                                                @input="onPerformerSearch"
                                                                @keydown="onPerformerKeydown"
                                                            />
                                                            <svg v-if="isLoadingPerformers" class="animate-spin w-3.5 h-3.5 text-white/30 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                                            </svg>
                                                        </div>
                                                        <p v-else class="text-white/30 text-xs py-1">Maximum of 4 performers reached</p>
                                                    </div>
                                                    <!-- Suggestions -->
                                                    <Teleport to="body">
                                                        <div
                                                            v-if="suggestions.length && performerDropdownStyle"
                                                            :style="performerDropdownStyle"
                                                            class="fixed z-[9999] bg-zinc-900 border border-white/10 rounded-xl overflow-y-auto casting-scroll shadow-2xl shadow-black/60"
                                                        >
                                                        <button
                                                            v-for="(performer, index) in suggestions"
                                                            :key="performer.id"
                                                            type="button"
                                                            @mousedown.prevent
                                                            @click="selectPerformer(performer)"
                                                            :class="['w-full text-left px-3 py-2 text-sm text-white transition-colors flex items-center gap-2.5', index === highlightedPerformerIndex ? 'bg-zinc-700' : 'hover:bg-zinc-800']"
                                                        >
                                                            <div class="w-9 h-9 rounded-full overflow-hidden ring-1 ring-white/10 shrink-0 bg-zinc-800">
                                                                <img :src="performer.thumbnail_url" :alt="performer.name" class="w-full h-full object-cover object-top scale-125 origin-top" />
                                                            </div>
                                                            <span>{{ performer.name }}</span>
                                                        </button>
                                                        </div>
                                                    </Teleport>
                                                    <p v-if="performerInput.trim().length >= 2 && !suggestions.length && !isLoadingPerformers" class="text-white/30 text-[11px] mt-1.5">No performers found</p>
                                                    <p v-if="fieldErrors.performers || performerLimitError" class="field-error-msg">{{ fieldErrors.performers || performerLimitError }}</p>
                                                </div>
                                            </div>


                                        </div>

                                        <!-- Performer Description -->
                                        <div v-if="!form.performers.length" class="mb-2">
                                            <div class="flex items-center gap-2 mb-2">
                                                <div class="flex-1 h-px bg-white/10" />
                                                <span class="text-white/30 text-[10px] font-bold uppercase tracking-wider">or name specific performers</span>
                                                <div class="flex-1 h-px bg-white/10" />
                                            </div>
                                            <input
                                                v-model="form.performerDescription"
                                                type="text"
                                                placeholder='e.g. "Brunette MILF, athletic male lead"'
                                                maxlength="200"
                                                :class="['field-input', fieldErrors.performer_description ? 'field-error' : '']"
                                            />
                                            <p v-if="fieldErrors.performer_description" class="field-error-msg">{{ fieldErrors.performer_description }}</p>
                                        </div>

                                        <p v-if="stepErrors.cast" class="field-error-msg mt-1">{{ stepErrors.cast }}</p>
                                    </template>

                                    <!-- ── Step 3: Scene Details ── -->
                                    <template v-else-if="currentStep === 3">
                                        <div class="flex items-center gap-3 mb-4 pt-1">
                                            <div class="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500/20 to-cyan-600/10 border border-teal-500/20 flex items-center justify-center shrink-0">
                                                <Film class="w-4.5 h-4.5 text-teal-400" />
                                            </div>
                                            <div>
                                                <h2 class="text-base font-bold text-white leading-tight">Scene Details</h2>
                                                <p class="text-white/35 text-[11px] leading-snug">Set the format, location, and categories.</p>
                                            </div>
                                        </div>

                                        <!-- Format -->
                                        <div class="mb-4">
                                            <label class="field-label">Format <span class="text-white/25 font-normal">(required)</span></label>
                                            <div class="grid grid-cols-4 gap-1.5 mt-1.5">
                                                <button
                                                    v-for="option in formatOptions"
                                                    :key="option"
                                                    type="button"
                                                    @click="form.format = form.format === option ? null : option"
                                                    :class="[
                                                        'py-2.5 rounded-xl text-xs font-semibold border transition-all duration-200 active:scale-95',
                                                        form.format === option
                                                            ? 'bg-teal-600/20 text-teal-300 border-teal-500/35 shadow-[0_0_12px_rgba(20,184,166,0.15)]'
                                                            : 'bg-white/[0.03] text-white/50 border-white/10 hover:border-white/15 hover:text-white/100 hover:bg-white/5'
                                                    ]"
                                                >
                                                    {{ option }}
                                                </button>
                                            </div>
                                            <p v-if="stepErrors.format || fieldErrors.format" class="field-error-msg">{{ stepErrors.format || fieldErrors.format }}</p>
                                        </div>

                                        <!-- Setting -->
                                        <div class="mb-4">
                                            <label class="field-label">Setting <span class="text-white/25 font-normal">(optional)</span></label>
                                            <p class="text-white/35 text-[11px] mb-2 leading-snug">Where does the scene take place?</p>
                                            <div class="flex flex-wrap gap-1.5 mb-2">
                                                <button
                                                    v-for="option in settingOptions"
                                                    :key="option.label"
                                                    type="button"
                                                    @click="toggleSetting(option.label)"
                                                    :class="[
                                                        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 active:scale-95',
                                                        form.setting.includes(option.label)
                                                            ? 'bg-teal-600/20 text-teal-300 border-teal-500/35'
                                                            : 'bg-white/[0.03] text-white/50 border-white/10 hover:border-white/15 hover:text-white/80'
                                                    ]"
                                                >
                                                    <span v-if="SETTING_PILL_DISPLAY === 'emoji'" class="text-sm leading-none">{{ option.emoji }}</span>
                                                    <component v-else :is="option.icon" :class="['w-3 h-3 shrink-0', form.setting.includes(option.label) ? 'opacity-100' : option.color]" />
                                                    {{ option.label }}
                                                </button>
                                                <button
                                                    type="button"
                                                    @click="showCustomSetting = !showCustomSetting"
                                                    :class="[
                                                        'px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 active:scale-95',
                                                        showCustomSetting
                                                            ? 'bg-teal-600/20 text-teal-300 border-teal-500/35'
                                                            : 'bg-white/[0.03] text-white/50 border-white/10 hover:border-white/15 hover:text-white/100'
                                                    ]"
                                                >
                                                    + Custom
                                                </button>
                                            </div>
                                            <input
                                                v-if="showCustomSetting"
                                                v-model="settingCustom"
                                                type="text"
                                                placeholder='Describe your setting — e.g. "Rooftop bar at sunset"'
                                                maxlength="120"
                                                :class="['field-input', fieldErrors.setting ? 'field-error' : '']"
                                            />
                                            <p v-if="fieldErrors.setting" class="field-error-msg">{{ fieldErrors.setting }}</p>
                                        </div>

                                        <!-- Tags -->
                                        <div class="mb-2">
                                            <label class="field-label">Category / Tags <span class="text-white/25 font-normal">(optional)</span></label>
                                            <p class="text-white/35 text-[11px] mb-1.5 leading-snug">Help people find your pitch — select all that apply.</p>
                                            <div ref="tagInputContainer" class="relative">
                                                <div :class="[
                                                    'bg-zinc-900/80 border rounded-xl px-3 py-2 transition-all',
                                                    tagInput.trim().length >= 2 && !tagSuggestions.length && !isLoadingTags
                                                        ? 'border-red-500/60'
                                                        : 'border-white/10 focus-within:border-violet-500/60 focus-within:shadow-[0_0_0_3px_rgba(139,92,246,0.08)]'
                                                ]">
                                                    <div v-if="form.tags.length" class="flex flex-wrap gap-1.5 mb-2">
                                                        <span
                                                            v-for="tag in form.tags"
                                                            :key="tag.id"
                                                            class="inline-flex items-center gap-1 bg-white/5 border border-white/10 text-white/60 text-xs font-medium px-2 py-0.5 rounded-full"
                                                        >
                                                            #{{ tag.name }}
                                                            <button type="button" @click="removeTag(tag.id)" class="text-white/25 hover:text-white/50 transition-colors">
                                                                <X class="w-3 h-3" />
                                                            </button>
                                                        </span>
                                                    </div>
                                                    <div class="flex items-center gap-2">
                                                        <input
                                                            v-model="tagInput"
                                                            type="text"
                                                            placeholder="Search tags…"
                                                            autocomplete="off"
                                                            class="flex-1 bg-transparent border-0 text-white text-sm placeholder:text-white/25 outline-none ring-0 focus:outline-none focus:ring-0"
                                                            @keydown.escape="tagInput = ''"
                                                        />
                                                        <svg v-if="isLoadingTags" class="animate-spin w-3.5 h-3.5 text-white/30 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                                                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                                        </svg>
                                                    </div>
                                                </div>
                                                <Teleport to="body">
                                                    <div
                                                        v-if="tagSuggestions.length && tagDropdownStyle"
                                                        :style="tagDropdownStyle"
                                                        class="fixed z-[9999] bg-zinc-900 border border-white/10 rounded-xl overflow-y-auto casting-scroll shadow-2xl shadow-black/60"
                                                    >
                                                    <button
                                                        v-for="tag in tagSuggestions"
                                                        :key="tag.id"
                                                        type="button"
                                                        @mousedown.prevent
                                                        @click="selectTag(tag)"
                                                        class="w-full text-left px-3 py-2 text-sm text-white/70 hover:bg-zinc-800 hover:text-white transition-colors"
                                                    >
                                                        #{{ tag.name }}
                                                    </button>
                                                    </div>
                                                </Teleport>
                                                <p v-if="tagInput.trim().length >= 2 && !tagSuggestions.length && !isLoadingTags" class="text-white/30 text-[11px] mt-1.5">No tags found</p>
                                            </div>
                                            <p v-if="fieldErrors.tags" class="field-error-msg mt-1">{{ fieldErrors.tags }}</p>
                                        </div>
                                    </template>

                                    <!-- ── Step 4: Finish ── -->
                                    <template v-else-if="currentStep === 4">
                                        <div class="flex items-center gap-3 mb-4 pt-1">
                                            <div class="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500/20 to-purple-600/10 border border-violet-500/20 flex items-center justify-center shrink-0">
                                                <Rocket class="w-4.5 h-4.5 text-violet-400" />
                                            </div>
                                            <div>
                                                <h2 class="text-base font-bold text-white leading-tight">Review your Pitch</h2>
                                                <p class="text-white/35 text-[11px] leading-snug">Double-check everything before submitting.</p>
                                            </div>
                                        </div>

                                        <!-- Summary -->
                                        <div class="mb-4 rounded-xl border border-white/10 bg-white/[0.02] divide-y divide-white/5 overflow-hidden">

                                            <!-- Title -->
                                            <div class="px-4 py-3 flex items-start justify-between gap-2">
                                                <div class="flex-1 min-w-0">
                                                    <p class="text-white/35 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Title</p>
                                                    <p class="text-white text-sm font-semibold">{{ form.title }}</p>
                                                </div>
                                                <button type="button" @click="goToStep(1)" class="shrink-0 w-6 h-6 rounded-md bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors mt-0.5">
                                                    <Pencil class="w-3 h-3 text-white/40" />
                                                </button>
                                            </div>

                                            <!-- The Setup -->
                                            <div class="px-4 py-3 flex items-start justify-between gap-2">
                                                <div class="flex-1 min-w-0">
                                                    <p class="text-white/35 text-[10px] font-semibold uppercase tracking-wider mb-0.5">The Setup</p>
                                                    <p class="text-white/70 text-xs leading-relaxed">{{ form.description }}</p>
                                                </div>
                                                <button type="button" @click="goToStep(1)" class="shrink-0 w-6 h-6 rounded-md bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors mt-0.5">
                                                    <Pencil class="w-3 h-3 text-white/40" />
                                                </button>
                                            </div>

                                            <!-- Director's Notes -->
                                            <div v-if="form.directorNotes" class="px-4 py-3 flex items-start justify-between gap-2">
                                                <div class="flex-1 min-w-0">
                                                    <p class="text-white/35 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Director's Notes</p>
                                                    <p class="text-white/70 text-xs leading-relaxed">{{ form.directorNotes }}</p>
                                                </div>
                                                <button type="button" @click="goToStep(1)" class="shrink-0 w-6 h-6 rounded-md bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors mt-0.5">
                                                    <Pencil class="w-3 h-3 text-white/40" />
                                                </button>
                                            </div>

                                            <!-- Cast -->
                                            <div class="px-4 py-3 flex items-start justify-between gap-2">
                                                <div class="flex-1 min-w-0">
                                                    <p class="text-white/35 text-[10px] font-semibold uppercase tracking-wider mb-1.5">Cast</p>
                                                    <div v-if="form.performers.length" class="flex flex-wrap gap-1.5">
                                                        <span
                                                            v-for="p in form.performers"
                                                            :key="p.id"
                                                            class="inline-flex items-center gap-1.5 bg-violet-600/15 border border-violet-500/25 text-violet-300 text-xs font-medium pl-0.5 pr-2 py-0.5 rounded-full"
                                                        >
                                                            <img :src="p.thumbnail_url" :alt="p.name" class="w-5 h-5 rounded-full object-cover object-top shrink-0" />
                                                            {{ p.name }}
                                                        </span>
                                                    </div>
                                                    <p v-else class="text-white/60 text-xs">{{ form.performerDescription }}</p>
                                                </div>
                                                <button type="button" @click="goToStep(2)" class="shrink-0 w-6 h-6 rounded-md bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors mt-0.5">
                                                    <Pencil class="w-3 h-3 text-white/40" />
                                                </button>
                                            </div>

                                            <!-- Format -->
                                            <div class="px-4 py-3 flex items-start justify-between gap-2">
                                                <div class="flex-1 min-w-0 flex items-start gap-3">
                                                    <div class="flex-1">
                                                        <p class="text-white/35 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Format</p>
                                                        <p class="text-teal-300 text-xs font-semibold">{{ form.format }}</p>
                                                    </div>
                                                    <div v-if="form.setting.length || settingCustom" class="flex-1">
                                                        <p class="text-white/35 text-[10px] font-semibold uppercase tracking-wider mb-1">Setting</p>
                                                        <div class="flex flex-wrap gap-1">
                                                            <span v-for="s in form.setting" :key="s" class="text-[10px] bg-teal-600/15 border border-teal-500/25 text-teal-300 px-1.5 py-0.5 rounded-full">{{ s }}</span>
                                                            <span v-if="showCustomSetting && settingCustom" class="text-[10px] bg-teal-600/15 border border-teal-500/25 text-teal-300 px-1.5 py-0.5 rounded-full">{{ settingCustom }}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <button type="button" @click="goToStep(3)" class="shrink-0 w-6 h-6 rounded-md bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors mt-0.5">
                                                    <Pencil class="w-3 h-3 text-white/40" />
                                                </button>
                                            </div>

                                            <!-- Tags -->
                                            <div v-if="form.tags.length" class="px-4 py-3 flex items-start justify-between gap-2">
                                                <div class="flex-1 min-w-0">
                                                    <p class="text-white/35 text-[10px] font-semibold uppercase tracking-wider mb-1.5">Tags</p>
                                                    <div class="flex flex-wrap gap-1">
                                                        <span v-for="tag in form.tags" :key="tag.id" class="text-[10px] bg-white/5 border border-white/10 text-white/55 px-1.5 py-0.5 rounded-full">#{{ tag.name }}</span>
                                                    </div>
                                                </div>
                                                <button type="button" @click="goToStep(3)" class="shrink-0 w-6 h-6 rounded-md bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors mt-0.5">
                                                    <Pencil class="w-3 h-3 text-white/40" />
                                                </button>
                                            </div>

                                        </div>

                                        <!-- Terms agreement -->
                                        <label class="mb-4 flex items-start gap-2.5 cursor-pointer w-fit group">
                                            <span
                                                :class="[
                                                    'mt-0.5 shrink-0 w-4 h-4 rounded border transition-all flex items-center justify-center',
                                                    agreedToTerms
                                                        ? 'bg-violet-600 border-violet-500 shadow-[0_0_8px_rgba(139,92,246,0.4)]'
                                                        : 'bg-transparent border-white/15 group-hover:border-white/30'
                                                ]"
                                                @click.prevent="agreedToTerms = !agreedToTerms"
                                            >
                                                <svg v-if="agreedToTerms" class="w-2.5 h-2.5 text-white" viewBox="0 0 10 8" fill="none">
                                                    <path d="M1 4l3 3 5-6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                                </svg>
                                            </span>
                                            <p class="text-white/40 text-[11px] leading-snug select-none" @click.prevent="agreedToTerms = !agreedToTerms">
                                                By submitting, I agree to the
                                                <a @click.stop href="https://www.naughtyamerica.com/terms-of-service.html" target="_blank" rel="noopener" class="text-violet-400 hover:text-violet-300 underline underline-offset-2">Terms of Service</a>
                                                and
                                                <a @click.stop href="https://www.naughtyamerica.com/privacy-policy.html" target="_blank" rel="noopener" class="text-violet-400 hover:text-violet-300 underline underline-offset-2">Privacy Policy</a>.
                                            </p>
                                        </label>

                                        <!-- Error -->
                                        <p v-if="errorMessage" class="text-red-400 text-sm text-center mb-3">{{ errorMessage }}</p>

                                        <!-- Similar casting warning -->
                                        <div v-if="similarCasting" class="mb-3 rounded-xl border border-amber-500/25 bg-gradient-to-b from-amber-500/10 to-amber-600/5 px-4 py-3.5">
                                            <div class="flex items-center gap-2 mb-1.5">
                                                <div class="w-1.5 h-1.5 rounded-full bg-amber-400" />
                                                <p class="text-amber-400 text-xs font-bold">Similar pitch already active</p>
                                            </div>
                                            <p class="text-white/50 text-xs leading-snug mb-3">
                                                "<span class="text-white/75">{{ similarCasting.title }}</span>" is already open and accepting support. Are you sure you want to submit a new one?
                                            </p>
                                            <div class="grid grid-cols-3 gap-1.5">
                                                <button @click="$emit('dismissSimilar')" class="py-2 border border-white/10 bg-white/[0.03] text-white/60 font-semibold rounded-lg text-xs hover:bg-white/[0.06] transition-all">Edit</button>
                                                <button @click="$emit('close')" class="py-2 border border-white/10 bg-transparent text-white/35 font-semibold rounded-lg text-xs hover:bg-white/[0.03] transition-all">Cancel</button>
                                                <button @click="handleSubmitForce" :disabled="loading" class="py-2 bg-amber-600 hover:bg-amber-500 text-white font-bold rounded-lg text-xs transition-all active:scale-95 disabled:opacity-50">Submit Anyway</button>
                                            </div>
                                        </div>
                                    </template>

                                </div>
                            </Transition>

                        <!-- ── Footer Navigation ── -->
                        <div class="shrink-0 px-5 pb-5 sm:px-6 sm:pb-6 pt-3 relative">
                            <!-- Top fade gradient over scroll content -->
                            <div class="absolute -top-6 inset-x-0 h-6 bg-gradient-to-t from-[#0a0a0f] to-transparent pointer-events-none" />

                            <div class="grid grid-cols-2 gap-2">
                                <button
                                    @click="goBack"
                                    class="py-3 border border-white/10 bg-white/[0.03] text-white/70 font-semibold rounded-xl text-xs hover:bg-white/[0.06] hover:text-white transition-all active:scale-95"
                                >
                                    {{ currentStep > 1 ? '← Back' : 'Cancel' }}
                                </button>

                                <!-- Continue -->
                                <button
                                    v-if="currentStep < TOTAL_STEPS"
                                    @click="goNext"
                                    :disabled="!canProceed"
                                    :class="[
                                        'py-3 font-bold rounded-xl text-xs transition-all duration-200',
                                        canProceed
                                            ? 'bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white shadow-lg shadow-violet-600/25 active:scale-[0.98]'
                                            : 'bg-white/5 text-white/20 cursor-not-allowed border border-white/5'
                                    ]"
                                >
                                    Continue →
                                </button>

                                <!-- Submit -->
                                <button
                                    v-else-if="!similarCasting"
                                    @click="handleSubmit"
                                    :disabled="!canSubmit || loading"
                                    :class="[
                                        'h-[46px] font-bold rounded-xl text-xs transition-all flex items-center justify-center gap-1.5',
                                        canSubmit && !loading
                                            ? 'bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white shadow-lg shadow-violet-600/25 active:scale-[0.98]'
                                            : 'bg-white/5 text-white/20 cursor-not-allowed border border-white/5'
                                    ]"
                                >
                                    <template v-if="loading">
                                        <svg class="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        Submitting…
                                    </template>
                                    <template v-else>
                                        <img :src="STAR_ICON" alt="" class="w-4 h-4" />
                                        <span>{{ castingCost }}</span>
                                        <span class="opacity-30">·</span>
                                        Submit Pitch
                                    </template>
                                </button>
                            </div>

                            <p v-if="currentStep === TOTAL_STEPS && !isUserLoggedIn" class="text-white/30 text-[11px] text-center mt-2.5 leading-snug">
                                You need a free account to submit your pitch
                            </p>
                        </div>

                    </template>

                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { X, Clapperboard, Check, Lightbulb, Users, Film, Rocket, Pencil, BedDouble, Briefcase, Waves, KeyRound, Dumbbell, UtensilsCrossed, Sofa, GraduationCap, Sparkles } from 'lucide-vue-next'
import axios from 'axios'
import { trackFathomEvent } from '@/Helpers/analytics'

const STAR_ICON = 'https://sm.naughtycdn.com/images/landers/prize/NA-Star.png'
const TOTAL_STEPS = 4

const steps = [
    { key: 'pitch',   label: 'The Pitch' },
    { key: 'cast',    label: 'The Cast' },
    { key: 'scene',   label: 'Scene' },
    { key: 'finish',  label: 'Finish' },
]

const successSteps = [
    'Your request enters a review queue where our team will assess feasibility and next steps.',
    'The more community support your casting gets, the stronger the signal to make it happen.',
    'Stay tuned — we\'ll keep you updated as your casting progresses.',
    'All submissions have 30 days from approval to meet their support goal. Submissions that do not meet the goal will be closed.',
]

// Draft cookie — persists form data across subdomains (www and members)
const DRAFT_COOKIE = 'casting_draft'

const saveDraftCookie = () => {
    const data = {
        form: form.value,
        showCustomSetting: showCustomSetting.value,
        settingCustom: settingCustom.value,
        showDirectorNotes: showDirectorNotes.value,
    }
    const expires = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toUTCString()
    const hostname = window.location.hostname
    let domainPart = ''
    if (hostname.includes('naughtyamerica.com')) {
        domainPart = '; domain=.naughtyamerica.com'
    }
    document.cookie = `${DRAFT_COOKIE}=${encodeURIComponent(JSON.stringify(data))}; expires=${expires}; path=/${domainPart}; SameSite=Lax`
}

const readDraftCookie = () => {
    const match = document.cookie.match(new RegExp('(?:^|; )' + DRAFT_COOKIE + '=([^;]*)'))
    if (!match) return null
    try {
        return JSON.parse(decodeURIComponent(match[1]))
    } catch {
        return null
    }
}

const clearDraftCookie = () => {
    const hostname = window.location.hostname
    let domainPart = ''
    if (hostname.includes('naughtyamerica.com')) {
        domainPart = '; domain=.naughtyamerica.com'
    }
    document.cookie = `${DRAFT_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/${domainPart}`
}

const props = defineProps({
    isOpen:        { type: Boolean, required: true },
    userBalance:   { type: Number,  required: true },
    loading:       { type: Boolean, default: false },
    errorMessage:  { type: String,  default: '' },
    fieldErrors:   { type: Object,  default: () => ({}) },
    submitted:     { type: Boolean, default: false },
    castingCost:   { type: Number,  default: 5 },
    similarCasting:{ type: Object,  default: null },
    isUserLoggedIn:{ type: Boolean, default: false },
})

const emit = defineEmits(['close', 'submit', 'dismissSimilar', 'buyStars'])

const formatOptions  = ['Standard', 'VR', 'Vertical', 'Mixed Reality']

// 'icon' | 'emoji'
const SETTING_PILL_DISPLAY = 'icon'

const settingOptions = [
    { label: 'Bedroom',          icon: BedDouble,        color: 'text-rose-400',    emoji: '🛏️' },
    { label: 'Office',           icon: Briefcase,        color: 'text-blue-400',    emoji: '💼' },
    { label: 'Pool / Outdoors',  icon: Waves,            color: 'text-cyan-400',    emoji: '🏊' },
    { label: 'Hotel Room',       icon: KeyRound,         color: 'text-amber-400',   emoji: '🏨' },
    { label: 'Gym / Locker Room',icon: Dumbbell,         color: 'text-orange-400',  emoji: '🏋️' },
    { label: 'Kitchen',          icon: UtensilsCrossed,  color: 'text-yellow-400',  emoji: '🍳' },
    { label: 'Living Room',      icon: Sofa,             color: 'text-violet-400',  emoji: '🛋️' },
    { label: 'Classroom',        icon: GraduationCap,    color: 'text-emerald-400', emoji: '📚' },
    { label: 'Massage Studio',   icon: Sparkles,         color: 'text-pink-400',    emoji: '💆' },
]


const currentStep       = ref(1)
const slideDirection    = ref('slide-left')
const showCustomSetting = ref(false)
const settingCustom     = ref('')
const showDirectorNotes = ref(false)
const agreedToTerms     = ref(false)
const stepErrors        = ref({})

const toggleSetting = (option) => {
    const idx = form.value.setting.indexOf(option)
    if (idx === -1) form.value.setting.push(option)
    else form.value.setting.splice(idx, 1)
}

const form = ref({
    title: '',
    performers: [],
    performerDescription: '',
    format: null,
    tags: [],
    description: '',
    setting: [],
    directorNotes: '',
    starsCommitted: props.castingCost,
})

// Performers
const performerInput            = ref('')
const allPerformers             = ref([])
const isLoadingPerformers       = ref(false)
const highlightedPerformerIndex = ref(-1)
const featuredPerformerId       = ref(null)
const performerInputContainer   = ref(null)

const performerDropdownStyle = computed(() => {
    if (!performerInputContainer.value) return null
    const rect = performerInputContainer.value.getBoundingClientRect()
    return {
        top: `${rect.bottom + 4}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        maxHeight: '200px',
        overflowY: 'auto'
    }
})


const loadPerformers = async () => {
    if (allPerformers.value.length > 0) return
    isLoadingPerformers.value = true
    try {
        const { data } = await axios.get('/feed/api/casting-performers')
        allPerformers.value = data
    } finally {
        isLoadingPerformers.value = false
    }
}

const suggestions = computed(() => {
    const q = performerInput.value.trim().toLowerCase()
    if (!q) return []
    const selectedIds = new Set(form.value.performers.map(p => p.id))
    return allPerformers.value
        .filter(p => !selectedIds.has(p.id) && p.name.toLowerCase().includes(q))
        .slice(0, 10)
})

const onPerformerSearch = () => {}

const performerLimitError = ref('')

const selectPerformer = (performer) => {
    if (form.value.performers.length >= 4) return
    form.value.performers.push(performer)
    form.value.performerDescription = ''
    performerInput.value = ''
    featuredPerformerId.value = performer.id
}

const removePerformer = (id) => {
    form.value.performers = form.value.performers.filter(p => p.id !== id)
    if (featuredPerformerId.value === id) {
        featuredPerformerId.value = form.value.performers[0]?.id ?? null
    }
    performerLimitError.value = ''
}

const closeSuggestions = () => {
    performerInput.value = ''
    highlightedPerformerIndex.value = -1
}

watch(suggestions, () => { highlightedPerformerIndex.value = -1 })

const onPerformerKeydown = (e) => {
    if (e.key === 'Escape') { closeSuggestions(); return }
    if (!suggestions.value.length) return
    if (e.key === 'ArrowDown') {
        e.preventDefault()
        highlightedPerformerIndex.value = Math.min(highlightedPerformerIndex.value + 1, suggestions.value.length - 1)
    } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        highlightedPerformerIndex.value = Math.max(highlightedPerformerIndex.value - 1, -1)
    } else if (e.key === 'Enter' && highlightedPerformerIndex.value >= 0) {
        e.preventDefault()
        selectPerformer(suggestions.value[highlightedPerformerIndex.value])
    }
}

// Tags
const tagInput         = ref('')
const allTags          = ref([])
const isLoadingTags    = ref(false)
const tagInputContainer = ref(null)

const tagDropdownStyle = computed(() => {
    if (!tagInputContainer.value) return null
    const rect = tagInputContainer.value.getBoundingClientRect()
    return {
        top: `${rect.bottom + 4}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        maxHeight: '200px',
        overflowY: 'auto'
    }
})

const loadTags = async () => {
    if (allTags.value.length > 0) return
    isLoadingTags.value = true
    try {
        const { data } = await axios.get('/feed/api/casting-tags')
        allTags.value = data
    } finally {
        isLoadingTags.value = false
    }
}

const tagSuggestions = computed(() => {
    const q = tagInput.value.trim().toLowerCase()
    if (!q) return []
    const selectedIds = new Set(form.value.tags.map(t => t.id))
    return allTags.value
        .filter(t => !selectedIds.has(t.id) && t.name.toLowerCase().includes(q))
        .slice(0, 10)
})

const selectTag = (tag) => { form.value.tags.push(tag); tagInput.value = '' }
const removeTag = (id)  => { form.value.tags = form.value.tags.filter(t => t.id !== id) }

const resetForm = () => {
    form.value = {
        title: '', performers: [], performerDescription: '',
        format: null, tags: [], description: '',
        setting: [], directorNotes: '', starsCommitted: props.castingCost,
    }
    performerInput.value = ''
    tagInput.value = ''
    showCustomSetting.value = false
    settingCustom.value = ''
    showDirectorNotes.value = false
    agreedToTerms.value = false
    currentStep.value = 1
    stepErrors.value = {}
}

watch(() => props.isOpen, (open) => {
    if (open) {
        document.body.style.overflow = 'hidden'
        loadPerformers()
        loadTags()
        const draft = readDraftCookie()
        if (draft) {
            form.value = { ...form.value, ...draft.form }
            showCustomSetting.value  = draft.showCustomSetting ?? false
            settingCustom.value      = draft.settingCustom ?? ''
            showDirectorNotes.value  = draft.showDirectorNotes ?? false
        }
    } else {
        document.body.style.overflow = ''
        resetForm()
    }
})

// Navigate to the first step that has a backend field error
watch(() => props.fieldErrors, (errors) => {
    if (!errors || !Object.keys(errors).length) return
    const step1Fields = ['title', 'description', 'director_notes']
    const step2Fields = ['performers', 'performer_description']
    const step3Fields = ['format', 'setting', 'tags']
    if (step1Fields.some(f => errors[f])) { slideDirection.value = 'slide-right'; currentStep.value = 1 }
    else if (step2Fields.some(f => errors[f])) { slideDirection.value = 'slide-right'; currentStep.value = 2 }
    else if (step3Fields.some(f => errors[f])) { slideDirection.value = 'slide-right'; currentStep.value = 3 }
})

// Per-step validation
const canProceed = computed(() => {
    if (currentStep.value === 1) return form.value.title.trim().length > 0 && form.value.description.trim().length > 0
    if (currentStep.value === 2) return form.value.performers.length > 0 || form.value.performerDescription.trim().length > 0
    if (currentStep.value === 3) return form.value.format !== null
    return true
})

const goNext = () => {
    stepErrors.value = {}
    if (currentStep.value === 1 && !canProceed.value) {
        if (!form.value.title.trim())       stepErrors.value.title       = 'Please add a title for your pitch.'
        if (!form.value.description.trim()) stepErrors.value.description = 'Please describe your concept.'
        return
    }
    if (currentStep.value === 2 && !canProceed.value) { stepErrors.value.cast   = 'Please select a performer or name specific performers.'; return }
    if (currentStep.value === 3 && !canProceed.value) { stepErrors.value.format = 'Please select a format.'; return }
    slideDirection.value = 'slide-left'
    currentStep.value++
    trackFathomEvent(`Casting Wizard Step ${currentStep.value} Reached`)
}

const goToStep = (step) => {
    slideDirection.value = 'slide-right'
    currentStep.value = step
}

const goBack = () => {
    if (currentStep.value > 1) {
        slideDirection.value = 'slide-right'
        currentStep.value--
    } else {
        handleClose()
    }
}

const hasEnoughBalance = computed(() => props.userBalance >= props.castingCost)
const canSubmit        = computed(() => agreedToTerms.value)

const handleSubmit = () => {
    if (!canSubmit.value) return
    saveDraftCookie()
    if (props.isUserLoggedIn && !hasEnoughBalance.value) { emit('buyStars'); return }
    const setting = [
        ...form.value.setting,
        ...(showCustomSetting.value && settingCustom.value.trim() ? [settingCustom.value.trim()] : []),
    ]
    trackFathomEvent('Casting Wizard Pitch Submitted')
    emit('submit', { ...form.value, setting })
}

const handleSubmitForce = () => {
    const setting = [
        ...form.value.setting,
        ...(showCustomSetting.value && settingCustom.value.trim() ? [settingCustom.value.trim()] : []),
    ]
    emit('submit', { ...form.value, setting, force: true })
}

// Touch/swipe to dismiss
const modalRef      = ref(null)
const touchStartY   = ref(0)
const touchCurrentY = ref(0)
const isDragging    = ref(false)

const modalStyle = computed(() => {
    if (!isDragging.value) return {}
    const deltaY = touchCurrentY.value - touchStartY.value
    if (deltaY < 0) return {}
    return { transform: `translateY(${deltaY}px)`, transition: 'none' }
})

const handleTouchStart = (e) => {
    const touch = e.touches[0]
    const rect  = modalRef.value?.getBoundingClientRect()
    if (!rect || touch.clientY - rect.top > 60) return
    touchStartY.value   = touch.clientY
    touchCurrentY.value = touch.clientY
    isDragging.value    = true
}

const handleTouchMove = (e) => {
    if (!isDragging.value) return
    touchCurrentY.value = e.touches[0].clientY
    if (touchCurrentY.value - touchStartY.value > 0) e.preventDefault()
}

const handleTouchEnd = () => {
    if (!isDragging.value) return
    if (touchCurrentY.value - touchStartY.value > 100) emit('close')
    isDragging.value    = false
    touchStartY.value   = 0
    touchCurrentY.value = 0
}

const handleClose = () => {
    clearDraftCookie()
    emit('close')
}

defineExpose({ clearDraftCookie })
</script>

<style scoped>
/* Modal background */
.wizard-modal {
    background: linear-gradient(160deg, #0f0f17 0%, #0a0a10 100%);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-bottom: none;
}
@media (min-width: 640px) {
    .wizard-modal {
        border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    }
}

/* Override global blue focus rule from app.css */
.wizard-modal textarea:focus,
.wizard-modal select:focus,
.wizard-modal input:focus,
.wizard-modal [type='text']:focus {
    border-color: rgba(139, 92, 246, 0.5) !important;
    --tw-ring-shadow: rgba(139, 92, 246, 0.08) !important;
}

/* Field primitives */
.field-label {
    @apply text-white/70 text-xs font-semibold block mb-1.5 tracking-wide;
}
.field-input {
    @apply w-full px-3.5 py-2.5 bg-zinc-900/70 border border-white/10 rounded-xl text-white text-sm
           placeholder:text-white/20 focus:outline-none focus:border-violet-500/50
           focus:shadow-[0_0_0_3px_rgba(139,92,246,0.08)] transition-all;
}
.field-error {
    @apply border-red-500/50 focus:border-red-500/70 focus:shadow-[0_0_0_3px_rgba(239,68,68,0.08)];
}
.field-error-msg {
    @apply text-red-400 text-xs mt-1;
}

/* Scroll */
.casting-scroll {
    scrollbar-width: thin;
    scrollbar-color: rgba(255,255,255,0.08) transparent;
}
.casting-scroll::-webkit-scrollbar       { width: 3px; }
.casting-scroll::-webkit-scrollbar-track { background: transparent; }
.casting-scroll::-webkit-scrollbar-thumb {
    background: rgba(255,255,255,0.08);
    border-radius: 9999px;
}
.casting-scroll::-webkit-scrollbar-thumb:hover {
    background: rgba(255,255,255,0.15);
}

/* Modal enter/leave */
.modal-enter-active, .modal-leave-active { transition: opacity 0.25s ease; }
.modal-enter-from,  .modal-leave-to      { opacity: 0; }

/* Featured performer card pop-in */
.performer-preview-enter-active { transition: opacity 0.2s ease, transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1); }
.performer-preview-leave-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.performer-preview-enter-from   { opacity: 0; transform: scale(0.85) translateY(8px); }
.performer-preview-leave-to     { opacity: 0; transform: scale(0.9); }

/* Image swap on rotation */
.performer-swap-enter-active { transition: opacity 0.6s ease, transform 0.6s ease; position: absolute; inset: 0; }
.performer-swap-leave-active { transition: opacity 0.6s ease, transform 0.6s ease; position: absolute; inset: 0; }
.performer-swap-enter-from   { opacity: 0; transform: scale(1.06); }
.performer-swap-leave-to     { opacity: 0; transform: scale(0.96); }

/* Name fade on rotation */
.performer-name-enter-active { transition: opacity 0.4s ease 0.2s; }
.performer-name-leave-active { transition: opacity 0.2s ease; position: absolute; inset: 0; }
.performer-name-enter-from   { opacity: 0; }
.performer-name-leave-to     { opacity: 0; }

/* Step slide transitions (mode="out-in" — no overlap, no absolute needed) */
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
    transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.22s ease;
}
.slide-left-enter-from  { transform: translateX(32px);  opacity: 0; }
.slide-left-leave-to    { transform: translateX(-32px); opacity: 0; }
.slide-right-enter-from { transform: translateX(-32px); opacity: 0; }
.slide-right-leave-to   { transform: translateX(32px);  opacity: 0; }
</style>
